import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlaidLinkOnSuccessMetadata } from 'react-plaid-link';
import { ErrorModal } from '@common-fe/common-fe';

import { SuccessModal } from '@/components/elements';
import ChoosePaymentMethod from '@/modules/transaction/components/ReimburseMe/ConfirmationStep/Immediately/ChoosePaymentMethod';
import { DebitCardModal } from '@/modules/transaction/components/ReimburseMe/ConfirmationStep/Immediately/DebitCart';
import DirectDeposit from '@/modules/transaction/components/ReimburseMe/ConfirmationStep/Immediately/DirectDeposit';
import { AddPayPalAccountModal } from '@/modules/transaction/components/ReimburseMe/ConfirmationStep/Immediately/PayPal';
import { AddVenmoAccountModal } from '@/modules/transaction/components/ReimburseMe/ConfirmationStep/Immediately/Venmo';
import useAddPaymentMethodQuery from '@/modules/transaction/components/ReimburseMe/queries/useAddPaymentMethod.query';
import useAddPlaidPaymentMethod from '@/modules/transaction/components/ReimburseMe/queries/useAddPlaidPaymentMethod.query';
import { useStore } from '@/modules/transaction/components/ReimburseMe/store/usePaymentMethods.store';
import {
  PaymentMethodOwnerType,
  PaymentMethodVerificationStatus,
} from '@/modules/transaction/components/ReimburseMe/types/payment.types';
import useHighlightedPaymentMethodStore from '@/modules/user/components/ManageProfile/components/ReimbursementOptions/stores/useHighlightedPaymentMethod.store';
import { PaymentMethodType } from '@/modules/user/user.types';
import useHighlightById from '@/utils/hooks/useHighlightById';
import { usePlaid } from '@/utils/plaid/hooks/usePlaid';
import PlaidModal from '@/utils/plaid/PlaidModal';
import { useDuplicatePlaidAccountStore } from '@/utils/plaid/store';

enum StepsStates {
  choosePaymentMethod = 'choosePaymentMethod',
  depositOptionsChoosing = 'depositOptionsChoosing',
  plaidOptionsChoosing = 'plaidOptionsChoosing',
  debitOptionsChoosing = 'debitOptionsChoosing',
  payPalOptionsChoosing = 'payPalOptionsChoosing',
  venmoOptionsChoosing = 'venmoOptionsChoosing',
  paymentMethodAdded = 'paymentMethodAdded',
}

interface Props {
  onSetVisible: (value: boolean) => void;
  refetchPaymentMethods?: () => void;
}

const AddPaymentMethodModal: React.FC<Props> = ({ onSetVisible, refetchPaymentMethods }) => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState('');
  const [step, setSteps] = useState(StepsStates.choosePaymentMethod);
  const [errorModal, setErrorModal] = useState(false);
  const [isPlaidLoading, setIsPlaidLoading] = useState(false);
  const { reset, setHighlightedPaymentMethodId, setHighlightedPaymentMethodIds } =
    useHighlightedPaymentMethodStore();
  const { save: createPaymentMethod } = useAddPaymentMethodQuery();

  const { setHighlightedById, setHighlightedByIds } = useHighlightById({
    onSetHighlightedId: setHighlightedPaymentMethodId,
    onSetHighlightedIds: setHighlightedPaymentMethodIds,
    reset,
  });

  const paymentMethods = useStore((state) => state.methods);
  const isPendingAccountExists = useMemo(() => {
    return paymentMethods?.some(
      (method) =>
        method.verificationStatus === PaymentMethodVerificationStatus.PENDING ||
        method.verificationStatus === PaymentMethodVerificationStatus.PENDING_MANUAL
    );
  }, [paymentMethods]);

  const handleClose = useCallback(() => {
    onSetVisible(false);
  }, [onSetVisible]);

  const onSuccess = useCallback(
    (paymentMethodId?: string, paymentMethodIds?: string[]) => {
      if (paymentMethodId) {
        setHighlightedById(paymentMethodId);
      }
      if (paymentMethodIds) {
        setHighlightedByIds(paymentMethodIds);
      }
      setSteps(StepsStates.paymentMethodAdded);
    },
    [setHighlightedById, setHighlightedByIds]
  );

  const { save } = useAddPlaidPaymentMethod(onSuccess);

  const setDuplicateAccountModal = useDuplicatePlaidAccountStore((state) => state.setIsDuplicateModalVisible);

  const handlePlaidPaymentMethodSave = useCallback(
    async (plaidMetadata: PlaidLinkOnSuccessMetadata) => {
      try {
        setIsPlaidLoading(true);
        await save({
          plaidMetadata,
        });
        setIsPlaidLoading(false);
        if (refetchPaymentMethods) {
          refetchPaymentMethods();
        }
      } catch (e) {
        // @ts-ignore
        if (e.response.status === 409) {
          setIsPlaidLoading(false);
          setDuplicateAccountModal(true);
          return;
        }
        setErrorModal(true);
        setIsPlaidLoading(false);
      }
    },
    [save, setDuplicateAccountModal, refetchPaymentMethods]
  );

  const { openPlaid, content } = usePlaid({
    onSuccess: handlePlaidPaymentMethodSave,
    onExit: () => setSteps(StepsStates.choosePaymentMethod),
    isPendingAccountExists,
  });

  const chooseCheckHandler = useCallback(async () => {
    try {
      const createdPaymentMethod = await createPaymentMethod({
        paymentType: PaymentMethodType.CHECK,
        paymentOwnerType: PaymentMethodOwnerType.EMPLOYEE,
      });
      setHighlightedById(`${createdPaymentMethod?.id}`);
      setTimeout(() => handleClose());
    } catch (error) {
      // @ts-ignore
      setErrorMessage(error?.response?.data?.elevate_error_message);
    }
  }, [createPaymentMethod, handleClose, setHighlightedById]);

  return (
    <>
      {step === StepsStates.choosePaymentMethod && (
        <ChoosePaymentMethod
          onClose={handleClose}
          chooseDeposit={() => {
            openPlaid();
            setSteps(StepsStates.plaidOptionsChoosing);
          }}
          chooseDebitCard={() => setSteps(StepsStates.debitOptionsChoosing)}
          choosePayPal={() => setSteps(StepsStates.payPalOptionsChoosing)}
          chooseVenmo={() => setSteps(StepsStates.venmoOptionsChoosing)}
          chooseCheck={chooseCheckHandler}
          onlyAddingMode
        />
      )}

      {step === StepsStates.plaidOptionsChoosing && content}

      {step === StepsStates.depositOptionsChoosing && (
        <DirectDeposit
          onClose={handleClose}
          onBack={() => setSteps(StepsStates.choosePaymentMethod)}
          onSuccess={onSuccess}
          hideNavigationButton
          allowSaveAsDefault
        />
      )}

      {step === StepsStates.debitOptionsChoosing && (
        <DebitCardModal
          onClose={handleClose}
          onBack={() => setSteps(StepsStates.choosePaymentMethod)}
          onSuccess={onSuccess}
          hideNavigationButton
          allowSaveAsDefault
        />
      )}

      {step === StepsStates.payPalOptionsChoosing && (
        <AddPayPalAccountModal
          onClose={handleClose}
          onBack={() => setSteps(StepsStates.choosePaymentMethod)}
          onSuccess={onSuccess}
          hideNavigationButton
          allowSaveAsDefault
        />
      )}

      {step === StepsStates.venmoOptionsChoosing && (
        <AddVenmoAccountModal
          onClose={handleClose}
          onBack={() => setSteps(StepsStates.choosePaymentMethod)}
          onSuccess={onSuccess}
          hideNavigationButton
          allowSaveAsDefault
        />
      )}

      {isPlaidLoading && <PlaidModal visible isLoading />}

      <SuccessModal
        header={t('Payment method added successfully!')}
        buttonText={t('Got It!')}
        visible={step === StepsStates.paymentMethodAdded}
        onSetVisible={handleClose}
        buttonStyle={{ width: '160px' }}
      />
      <ErrorModal
        testId="payment-methods-error-modal"
        visible={errorModal}
        onSetVisible={() => {
          setErrorModal(false);
          handleClose();
        }}
        header={t('Something went wrong')}
        helpText={t('Try again later')}
        buttonText={t('Got It!')}
        onSubmit={() => {
          setErrorModal(false);
          handleClose();
        }}
      />

      {errorMessage ? (
        <ErrorModal
          testId="AddPaymentMethodModal-error"
          visible
          header="Error"
          helpText={errorMessage}
          buttonText="Close"
          onSetVisible={() => {
            setErrorMessage('');
          }}
        />
      ) : null}
    </>
  );
};

export default AddPaymentMethodModal;
